import { useShapesList } from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { isDrawGeometryType } from '@ais-ui/react-openlayers';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardClose,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  InfiniteScroll,
  Input,
  LoadingSpinner,
  Separator,
  TruncatedText,
} from '@ais-ui/ui-core';
import { cn, isNonEmpty } from '@ais-ui/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapShapeMenu } from './MapShapeMenu';
import { DeleteShape } from './Mutations/DeleteShape';
import { EditShapeGroup } from './Mutations/EditShapeGroup';
import { setParentIdsOnShapes } from './utils';
import { shapeToIcon } from './shapeToIcon';
import { useOnMapPanelExit } from '../Map/MapPanel/useOnMapPanelExit';

import { v4 as uuidv4 } from 'uuid';
import { useLocalization } from '@ais-ui/locale-management';
import {
  NoItemsEmptyStateList,
  NoResultsEmptyStateList,
} from '../../components/EmptyList';
import { EditShape } from './Mutations/EditShape';
import { ShapeFilter } from './ShapeFilter';
import { useShapeFilters } from './useShapeFilters';
import { ShapeDropdownMenu } from './Dropdown/ShapeDropdownMenu';
import { ShowHideShapeButton } from './ShowHideShapeButton';
import { ShowOnStartupButton } from './ShowOnStartupButton';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ShapeSort } from './ShapeSort';

interface ShapeListProps {
  className: string;
}

export const Shapes = React.memo(({ className }: ShapeListProps) => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());

  const { data, isLoading } = useShapesList();

  const onExit = useOnMapPanelExit();

  const shapes = useMemo(() => {
    return data?.records || [];
  }, [data?.records]);

  const {
    filteredShapes,
    shapeFilters,
    setShapeFilters,
    sortConfig,
    setSortConfig,
    findParentCollectionsToExpand,
  } = useShapeFilters(shapes, search);

  // Optimized accordion change handler
  const handleAccordionChange = useCallback((values: string[]) => {
    setOpenAccordions((prev) => {
      const newSet = new Set(values);
      // Only update if actually different
      if (prev.size !== newSet.size) return newSet;
      for (const value of prev) {
        if (!newSet.has(value)) return newSet;
      }
      for (const value of newSet) {
        if (!prev.has(value)) return newSet;
      }
      return prev; // Return same reference if no change
    });
  }, []);

  // Optimized search effect
  useEffect(() => {
    if (search.length >= 3) {
      const parentsToOpen = findParentCollectionsToExpand(
        filteredShapes,
        search,
      );
      if (parentsToOpen.size > 0) {
        setOpenAccordions((prev) => {
          const newSet = new Set([...prev, ...parentsToOpen]);
          // Only update if actually different
          if (newSet.size === prev.size) {
            // Check if all values are the same
            for (const value of newSet) {
              if (!prev.has(value)) return newSet;
            }
            return prev;
          }
          return newSet;
        });
      }
    }
  }, [search, filteredShapes, findParentCollectionsToExpand]);

  useEffect(() => {
    setParentIdsOnShapes(shapes);
  }, [shapes]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value;

      // Update search immediately for responsive UI
      setSearch(newValue);

      // Clear accordion state when search becomes less than 3 characters
      if (newValue.length < 3) {
        setOpenAccordions(new Set());
      }
    },
    [],
  );

  // Memoize the accordion value array to prevent unnecessary re-renders
  const accordionValues = useMemo(
    () => Array.from(openAccordions),
    [openAccordions],
  );

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader variant="tinted">
        <CardClose onClick={onExit} />
        <div className="space-y-2">
          <CardTitle className="text-lg font-medium text-muted-foreground">
            {messages.shapesListHeader}
          </CardTitle>
          <CardDescription>{messages.shapesListSubheader}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex gap-2">
        <Input
          id="search"
          placeholder={messages.shapesListFindShape}
          className="h-10 flex-1"
          value={search}
          onChange={handleSearchChange}
        />
        <ShapeSort sortConfig={sortConfig} setSortConfig={setSortConfig} />
        <ShapeFilter
          shapeFilters={shapeFilters}
          setShapeFilters={setShapeFilters}
          shapes={shapes}
        />
      </CardContent>
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        {!isLoading && shapes?.length === 0 && (
          <NoItemsEmptyStateList entity="shape" srcImage="empty_list_bro.png">
            <MapShapeMenu buttonText="Create shape" className="w-full h-10" />
          </NoItemsEmptyStateList>
        )}
        {isLoading && <LoadingSpinner />}
        {!isLoading && shapes?.length !== 0 && filteredShapes.length === 0 && (
          <NoResultsEmptyStateList
            entity="shape"
            srcImage="empty_list_bro.png"
          />
        )}
        {!isLoading && filteredShapes?.length !== 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="multiple"
            value={accordionValues}
            onValueChange={handleAccordionChange}
          >
            <InfiniteScroll
              items={filteredShapes}
              limit={30}
              observeResize={true}
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              renderItem={(shape) => {
                return (
                  <ShapeItem
                    key={shape.properties?.id}
                    shape={shape}
                    level={1}
                    owner={shape.properties?.owner}
                    sharedWith={shape.properties?.acl}
                    openAccordions={openAccordions}
                    onAccordionChange={handleAccordionChange}
                  />
                );
              }}
            ></InfiniteScroll>
          </Accordion>
        )}
      </CardContent>
      <CardFooter className="">
        {shapes?.length !== 0 && (
          <MapShapeMenu buttonText={messages.genericCreate} />
        )}
      </CardFooter>
    </Card>
  );
});

Shapes.displayName = 'Shapes';

interface ShapeGridItemProps {
  label: string;
  value: string | undefined | null;
}

const ShapeGridItem = React.memo(({ label, value }: ShapeGridItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
});

ShapeGridItem.displayName = 'ShapeGridItem';

interface ShapeItemProps {
  shape: GeoJson;
  level: number;
  owner: string | undefined;
  sharedWith: string[] | undefined;
  openAccordions: Set<string>;
  onAccordionChange: (values: string[]) => void;
}

const INITIAL_NESTED_ITEMS_LIMIT = 30;

const ShapeItem = React.memo(
  ({
    shape,
    level,
    owner,
    sharedWith,
    openAccordions,
    onAccordionChange,
  }: ShapeItemProps) => {
    const { messages } = useLocalization();
    const [showDeleteShapeDialog, setShowDeleteShapeDialog] =
      useState<boolean>(false);
    const [showEditShapeGroupDialog, setShowEditShapeGroupDialog] =
      useState<boolean>(false);
    const [showEditShapeDialog, setShowEditShapeDialog] =
      useState<boolean>(false);
    const [expandedItems, setExpandedItems] = useState<number>(
      INITIAL_NESTED_ITEMS_LIMIT,
    );

    const ShapeIcon = shapeToIcon(shape);
    const shapeId = shape.properties?.id || uuidv4();

    const onEdit = useCallback(() => {
      if (shape.type === 'Feature') {
        const geometryType = shape.geometry?.type;

        // Make sure that we are not trying to update a FeatureCollection
        if (!geometryType || !isDrawGeometryType(geometryType)) {
          return;
        }
        setShowEditShapeDialog(true);
      } else {
        setShowEditShapeGroupDialog(true);
      }
    }, [shape]);

    const onDeleteShow = useCallback(() => {
      setShowDeleteShapeDialog(true);
    }, []);

    // Calculate visible features and remaining count
    const visibleFeatures = useMemo(() => {
      if (shape.type === 'FeatureCollection' && shape.features) {
        return shape.features.slice(0, expandedItems);
      }
      return [];
    }, [shape, expandedItems]);

    const remainingCount = useMemo(() => {
      if (shape.type === 'FeatureCollection' && shape.features) {
        return Math.max(0, shape.features.length - expandedItems);
      }
      return 0;
    }, [shape, expandedItems]);

    const totalCount =
      shape.type === 'FeatureCollection' ? shape.features?.length || 0 : 0;

    const handleLoadMore = useCallback(() => {
      setExpandedItems((prev) => prev + INITIAL_NESTED_ITEMS_LIMIT);
    }, []);

    const handleShowLess = useCallback(() => {
      setExpandedItems(INITIAL_NESTED_ITEMS_LIMIT);
    }, []);

    // Calculate maxWidth based on nesting level to account for indentation
    // Reduced base width to prevent overflow into kebab menu column
    // Use smaller width for FeatureCollections to accommodate the count display
    const maxWidth = useMemo(() => {
      const baseWidth = shape.type === 'FeatureCollection' ? 140 : 160;
      const widthReductionPerLevel = 20;
      return `${baseWidth - (level - 1) * widthReductionPerLevel}px`;
    }, [level, shape.type]);

    return (
      <AccordionItem key={shapeId} value={shapeId} className={cn('pr-2 pl-2')}>
        <div
          className={cn('grid grid-cols-[1fr_34px] items-center w-full pr-2')}
        >
          <div className="flex items-center min-w-0">
            <div className="flex items-center flex-shrink-0">
              <ShowOnStartupButton shape={shape} />
              <ShowHideShapeButton shape={shape} />
              <Separator
                orientation="vertical"
                className="h-6 bg-border ml-1"
              />
            </div>
            <AccordionTrigger className="min-w-0 flex-1" hideChevron={true}>
              <div className="flex space-x-2 items-center ml-4 min-w-0">
                <ShapeIcon className="size-4 flex-shrink-0" />
                <TruncatedText
                  text={shape.properties.name || ''}
                  className="text-sm text-left font-medium leading-none group-hover:underline"
                  maxWidth={maxWidth}
                />
                {totalCount > 0 && (
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    ({totalCount})
                  </span>
                )}
              </div>
            </AccordionTrigger>
          </div>
          <div className="flex justify-end items-center">
            <ShapeDropdownMenu
              shape={shape}
              onDeleteShow={onDeleteShow}
              onEdit={onEdit}
            />
          </div>
        </div>

        <AccordionContent>
          <div className="grid gap-6 px-4 pt-4">
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <ShapeGridItem label={messages.shapesItemOwner} value={owner} />
                <ShapeGridItem
                  label={messages.shapesItemShared}
                  value={sharedWith?.join(', ') || 'not shared'}
                />
              </div>
            </div>
          </div>
          {shape.type === 'FeatureCollection' && visibleFeatures.length > 0 && (
            <div className="mt-4">
              <div className="p-0 my-4 overflow-y-auto border rounded-lg">
                {visibleFeatures.map((feature) => (
                  <ShapeItem
                    key={feature.properties?.id}
                    shape={feature}
                    level={level + 1}
                    owner={owner}
                    sharedWith={sharedWith}
                    openAccordions={openAccordions}
                    onAccordionChange={onAccordionChange}
                  />
                ))}
              </div>

              {(remainingCount > 0 ||
                expandedItems > INITIAL_NESTED_ITEMS_LIMIT) && (
                <div className="flex flex-col items-start gap-2 mt-2 mb-4 w-46">
                  {remainingCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLoadMore}
                      className="text-xs"
                    >
                      <ChevronDown className="size-3 mr-1" />
                      Show{' '}
                      {Math.min(
                        remainingCount,
                        INITIAL_NESTED_ITEMS_LIMIT,
                      )}{' '}
                      more
                      {remainingCount > INITIAL_NESTED_ITEMS_LIMIT &&
                        ` (${remainingCount} remaining)`}
                    </Button>
                  )}
                  {expandedItems > INITIAL_NESTED_ITEMS_LIMIT && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShowLess}
                      className="text-xs"
                    >
                      <ChevronUp className="size-3 mr-1" />
                      Show less
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </AccordionContent>
        <DeleteShape
          shape={shape}
          showDialog={showDeleteShapeDialog}
          setShowDialog={setShowDeleteShapeDialog}
        />
        {showEditShapeDialog && (
          <EditShape setShowDialog={setShowEditShapeDialog} shape={shape} />
        )}
        {showEditShapeGroupDialog && (
          <EditShapeGroup
            showDialog={showEditShapeGroupDialog}
            setShowDialog={setShowEditShapeGroupDialog}
            shape={shape}
          />
        )}
      </AccordionItem>
    );
  },
  // Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    // Compare primitive values
    if (
      prevProps.level !== nextProps.level ||
      prevProps.owner !== nextProps.owner ||
      prevProps.shape.properties?.id !== nextProps.shape.properties?.id ||
      prevProps.shape.properties?.name !== nextProps.shape.properties?.name ||
      prevProps.shape.type !== nextProps.shape.type
    ) {
      return false;
    }

    // Compare arrays
    if (
      JSON.stringify(prevProps.sharedWith) !==
      JSON.stringify(nextProps.sharedWith)
    ) {
      return false;
    }

    // Compare Set contents - only check if THIS shape's accordion state changed
    const shapeId = prevProps.shape.properties?.id;
    if (shapeId) {
      const prevOpen = prevProps.openAccordions.has(shapeId);
      const nextOpen = nextProps.openAccordions.has(shapeId);
      if (prevOpen !== nextOpen) {
        return false;
      }
    }

    // If this is a FeatureCollection, also check if any child shapes changed their accordion state
    if (
      prevProps.shape.type === 'FeatureCollection' &&
      prevProps.shape.features
    ) {
      // Compare features length to detect add/delete of nested shapes
      const prevFeaturesLength = prevProps.shape.features?.length ?? 0;
      const nextFeaturesLength = nextProps.shape.features?.length ?? 0;
      if (prevFeaturesLength !== nextFeaturesLength) {
        return false;
      }

      // Only check the first 30 items for accordion state changes
      const itemsToCheck = Math.min(30, prevProps.shape.features.length);
      for (let i = 0; i < itemsToCheck; i++) {
        const feature = prevProps.shape.features[i];
        const featureId = feature.properties?.id;
        if (featureId) {
          const prevChildOpen = prevProps.openAccordions.has(featureId);
          const nextChildOpen = nextProps.openAccordions.has(featureId);
          if (prevChildOpen !== nextChildOpen) {
            return false;
          }
        }
      }
    }

    return true;
  },
);

ShapeItem.displayName = 'ShapeItem';
